import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, FormControl, MenuItem, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateSearch from '../../../../../components/DateSearch';
import { dataTestIds } from '../../../../../constants/data-test-ids';
import { PartialSearchOptionsState, SearchOptionsFilters } from './types';
import { useLocationsOptions } from './useLocationsOptions';

export const PatientsSearchFilters: React.FC<{
  searchFilters: SearchOptionsFilters;
  setSearchField: ({ field, value }: { field: keyof SearchOptionsFilters; value: string }) => void;
  resetFilters: () => void;
  search: (overriddenParams?: PartialSearchOptionsState) => void;
}> = ({ searchFilters, setSearchField, resetFilters: resetFilters, search }) => {
  const [showAdditionalSearch, setShowAdditionalSearch] = useState(true);
  const { location: locationOptions } = useLocationsOptions();
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    search({ pagination: { offset: 0 } });
  };

  return (
    <FormControl component="form" onSubmit={handleSubmit} fullWidth>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <TextField
          data-testid={dataTestIds.patients.searchByLastNameField}
          sx={{ flex: 1 }}
          label={t('patients.filters.lastName')}
          placeholder="Doe"
          value={searchFilters.lastName}
          onChange={(e) => setSearchField({ field: 'lastName', value: e.target.value })}
        />
        <TextField
          data-testid={dataTestIds.patients.searchByGivenNamesField}
          sx={{ flex: 1 }}
          label={t('patients.filters.givenNames')}
          placeholder="John Henry"
          value={searchFilters.givenNames}
          onChange={(e) => setSearchField({ field: 'givenNames', value: e.target.value })}
        />
        <Box sx={{ flex: 1 }}>
          <DateSearch
            date={searchFilters.dob ? DateTime.fromISO(searchFilters.dob) : null}
            setDate={(date: DateTime | null) =>
              setSearchField({ field: 'dob', value: date ? date.toISODate() || '' : '' })
            }
            label={t('patients.filters.dob')}
            closeOnSelect
            data-testid={dataTestIds.patients.searchByDateOfBirthField}
          />
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Button
          type="button"
          onClick={() => setShowAdditionalSearch(!showAdditionalSearch)}
          startIcon={showAdditionalSearch ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          color="primary"
        >
          {t('patients.filters.additionalSearch')}
        </Button>
      </Box>

      {showAdditionalSearch && (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}
        >
          <TextField
            label={t('patients.filters.phone')}
            data-testid={dataTestIds.patients.searchByPhoneField}
            placeholder="(XXX) XX-XXXX"
            value={searchFilters.phone}
            onChange={(e) => setSearchField({ field: 'phone', value: e.target.value })}
          />
          <TextField
            label={t('patients.filters.address')}
            data-testid={dataTestIds.patients.searchByAddressField}
            value={searchFilters.address}
            onChange={(e) => setSearchField({ field: 'address', value: e.target.value })}
          />
          <TextField
            label={t('patients.filters.email')}
            data-testid={dataTestIds.patients.searchByEmailField}
            placeholder="example@mail.com"
            value={searchFilters.email}
            onChange={(e) => setSearchField({ field: 'email', value: e.target.value })}
          />
          {/* <TextField
                label="Subscriber Number (Insurance)"
                value={searchFilters.subscriberNumber}
                onChange={(e) => setSearchField({ field: 'subscriberNumber', value: e.target.value })}
              /> */}
          <FormControl fullWidth>
            <TextField
              data-testid={dataTestIds.patients.searchByStatusName}
              select
              label={t('patients.filters.status')}
              value={searchFilters.status}
              onChange={(e) => setSearchField({ field: 'status', value: e.target.value })}
            >
              <MenuItem value="All">{t('patients.filters.all')}</MenuItem>
              <MenuItem value="Active">{t('patients.filters.active')}</MenuItem>
              <MenuItem value="Deceased">{t('patients.filters.deceased')}</MenuItem>
              <MenuItem value="Inactive">{t('patients.filters.inactive')}</MenuItem>
            </TextField>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              data-testid={dataTestIds.patients.searchByLocationName}
              select
              label={t('patients.filters.location')}
              value={searchFilters.location}
              onChange={(e) => setSearchField({ field: 'location', value: e.target.value })}
            >
              <MenuItem value="All">{t('patients.filters.all')}</MenuItem>
              {locationOptions.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'end', gap: 3 }}>
        <Button type="button" onClick={resetFilters} data-testid={dataTestIds.patients.resetFiltersButton}>
          {t('patients.filters.reset')}
        </Button>
        <Button
          data-testid={dataTestIds.patients.searchButton}
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          type="submit"
          sx={{ mr: 1, borderRadius: 28 }}
        >
          {t('patients.filters.search')}
        </Button>
      </Box>
    </FormControl>
  );
};
